// Slides do banner da tela inicial: imagem + link de redirecionamento.
//
// Puro (sem firebase/RN) para ser testável. Existe porque a tela montava as
// duas listas SEPARADAMENTE e as cruzava por POSIÇÃO — e as duas não são
// paralelas:
//
//   imageApp.url        = { '0': fotoA, '2': fotoC }   ← slot 1 apagado
//   imageApp.navigation = { '0': linkA, '2': linkC }
//
// `Object.values(url)` devolve [fotoA, fotoC], então o segundo slide passa a
// ser a posição 1 do array — mas o link continua guardado na chave '2'. A tela
// lia `navigation[1]`, que não existe, e o slide ficava sem link nenhum.
// Apagar a imagem do meio (o painel usa `deleteField()`, que deixa o buraco)
// bastava para o redirecionamento parar de funcionar.
//
// Aqui a imagem e o link são casados pela CHAVE DO SLOT, que é o que o painel
// de fato grava, e não pela posição na lista.

// Um esquema de URL já escrito: 'https:', 'mailto:', 'tel:', 'meuapp:'…
const HAS_SCHEME = /^[a-z][a-z0-9+.-]*:/i;

// Algo com cara de domínio antes da primeira barra ou interrogação —
// 'exemplo.com', 'www.exemplo.com/promo', 'sub.exemplo.com.br?x=1'.
const LOOKS_LIKE_DOMAIN = /^[^/?#\s]+\.[^/?#\s]+/;

// Completa o esquema quando o admin digita só o domínio.
//
// Sem isso, `Linking.openURL('www.exemplo.com')` na web cai em
// `window.open('www.exemplo.com')`, que o navegador trata como caminho
// RELATIVO: em vez de abrir o site, ele navega para
// `https://seuapp.com/www.exemplo.com` — uma rota que não existe. No aplicativo
// nativo é pior, a chamada simplesmente falha por não haver esquema.
// O campo do painel só sugere "https://..." como placeholder, e digitar o
// domínio puro é o erro mais natural do mundo.
export const normalizeBannerLink = (raw) => {
  const link = String(raw ?? '').trim();
  if (!link) return '';

  if (HAS_SCHEME.test(link)) return link;
  if (link.startsWith('//')) return `https:${link}`;
  if (LOOKS_LIKE_DOMAIN.test(link)) return `https://${link}`;

  // Não parece link. Devolve como está em vez de inventar um esquema: virar
  // "https://clique aqui" não ajudaria ninguém, e adivinhar menos ainda.
  return link;
};

// Lista de slides na ordem dos slots do painel, já com o link resolvido.
//
// Aceita as duas formas em que `url` aparece no Firestore: o mapa por slot
// (`{ '0': ..., '1': ... }`, gravado pelo upload) e a string única de instalações
// antigas, de antes de o banner virar carrossel.
export const buildBannerSlides = (imageApp) => {
  const urls = imageApp?.url;
  const links = imageApp?.navigation;

  const linkFor = (key) => normalizeBannerLink(links?.[key]);

  if (typeof urls === 'string') {
    return urls ? [{ url: urls, link: linkFor('0') }] : [];
  }

  if (!urls || typeof urls !== 'object') return [];

  return Object.keys(urls)
    // Ordem dos slots, numérica: '10' não pode vir antes de '2' (a ordenação
    // padrão de strings faria isso).
    .sort((a, b) => Number(a) - Number(b))
    .filter((key) => typeof urls[key] === 'string' && urls[key].trim())
    .map((key) => ({ url: urls[key], link: linkFor(key) }));
};
